import React from 'react';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import {
  topNavItems,
  primaryNavItems,
  platformNavItems,
  secondaryNavItems,
  type NavigationItem,
} from '../navigationItems';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarNavGroup } from './SidebarNavGroup';
import { useSidebarDensity } from './constants';
import { useOnboardingStatus } from '../../../../features/onboarding/hooks/useOnboardingStatus';
import { usePermission } from '@billda/permissions-ui';
import { Permissions } from '@billda/permissions-ui';
import { useFeature, Features } from '@billda/features-ui';

interface SidebarContentProps {
  permissions: string[];
  isActive: (path: string) => boolean;
  collapsedGroups: string[];
  onToggleCollapsed: (group: string) => void;
  onNavigate: (item: NavigationItem) => void;
  onSearch: () => void;
}

export const SidebarContent: React.FC<SidebarContentProps> = ({
  permissions,
  isActive,
  collapsedGroups,
  onToggleCollapsed,
  onNavigate,
  onSearch,
}) => {
  const { navItemSx, iconSx, ICON_SIZE } = useSidebarDensity();
  const hasOrgManage = usePermission(Permissions.Organisation.Manage);
  const { data: onboardingStatus } = useOnboardingStatus(hasOrgManage);
  const isDashboardEnabled = useFeature(Features.Dashboard.IsEnabled);

  const filteredTopNavItems = React.useMemo(() => {
    if (onboardingStatus?.gettingStartedCompletedAt) {
      return topNavItems.filter(item => item.text !== 'Getting Started');
    }
    return topNavItems;
  }, [onboardingStatus?.gettingStartedCompletedAt]);

  return (
    <>
      {/* Scrollable primary nav items */}
      <Box sx={{ flex: 1, overflow: 'auto', pt: 1, pb: 1 }}>
        <List disablePadding>
          {/* Dashboard (coming soon) */}
          {!isDashboardEnabled && (
            <ListItemButton
              data-testid="expanded-nav-dashboard"
              sx={{
                ...navItemSx,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              <ListItemIcon sx={{ ...iconSx, color: 'text.secondary' }}>
                <DashboardIcon sx={{ fontSize: ICON_SIZE }} />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{ variant: 'body2' }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.tertiary',
                  whiteSpace: 'nowrap',
                }}
              >
                Soon
              </Typography>
            </ListItemButton>
          )}

          {/* Search */}
          <ListItemButton
            onClick={onSearch}
            data-testid="expanded-nav-search"
            sx={{
              ...navItemSx,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            <ListItemIcon sx={{ ...iconSx, color: 'text.secondary' }}>
              <SearchIcon sx={{ fontSize: ICON_SIZE }} />
            </ListItemIcon>
            <ListItemText
              primary="Search"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItemButton>

          {/* Top-level items (e.g. Getting Started) */}
          {filteredTopNavItems.map(item => (
            <SidebarNavItem
              key={item.text}
              item={item}
              permissions={permissions}
              isActive={isActive(item.path)}
              onNavigate={onNavigate}
            />
          ))}

          {/* Primary groups / items */}

          {/* Platform admin groups */}
          {platformNavItems.map(item =>
            item.subItems?.length ? (
              <SidebarNavGroup
                key={item.text}
                item={item}
                permissions={permissions}
                isActive={isActive}
                isCollapsed={collapsedGroups.includes(item.text)}
                onToggleCollapsed={onToggleCollapsed}
                onNavigate={onNavigate}
              />
            ) : (
              <SidebarNavItem
                key={item.text}
                item={item}
                permissions={permissions}
                isActive={isActive(item.path)}
                onNavigate={onNavigate}
              />
            )
          )}

          {primaryNavItems.map(item =>
            item.subItems?.length ? (
              <SidebarNavGroup
                key={item.text}
                item={item}
                permissions={permissions}
                isActive={isActive}
                isCollapsed={collapsedGroups.includes(item.text)}
                onToggleCollapsed={onToggleCollapsed}
                onNavigate={onNavigate}
              />
            ) : (
              <SidebarNavItem
                key={item.text}
                item={item}
                permissions={permissions}
                isActive={isActive(item.path)}
                onNavigate={onNavigate}
              />
            )
          )}
        </List>
      </Box>

      {/* Secondary nav */}
      <Divider />
      <Box sx={{ py: 0.5 }}>
        <List disablePadding>
          {secondaryNavItems.map(item => (
            <SidebarNavItem
              key={item.text}
              item={item}
              permissions={permissions}
              isActive={isActive(item.path)}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Box>
    </>
  );
};
